import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AppointmentView,
  AvailabilityView,
  BotProfileView,
  ClientProfileView,
  ServiceView,
  SlotView,
} from '@masterzapis/types';
import { ApiError, api } from './api';
import {
  confirmAction,
  hapticError,
  hapticSuccess,
  isInsideTelegram,
  resolveClientBotId,
  tg,
} from './telegram';
import { ServicePicker } from './components/ServicePicker';
import { TimePicker } from './components/TimePicker';
import { ConfirmBooking } from './components/ConfirmBooking';
import { MyAppointments } from './components/MyAppointments';
import { OutsideTelegram } from './components/OutsideTelegram';

/**
 * Вне Telegram приложение работает только на dev-сервере: там подпись
 * пользователя подменяется заголовком, а в продакшене такой вход закрыт.
 */
const canWorkOutsideTelegram = import.meta.env.DEV;

type Tab = 'book' | 'my';
type Step = 'service' | 'time' | 'confirm' | 'success';

export function App() {
  const botId = useMemo(resolveClientBotId, []);
  const outsideTelegram = !isInsideTelegram && !canWorkOutsideTelegram;

  const [profile, setProfile] = useState<BotProfileView | null>(null);
  const [services, setServices] = useState<ServiceView[]>([]);
  const [me, setMe] = useState<ClientProfileView | null>(null);
  const [appointments, setAppointments] = useState<AppointmentView[]>([]);

  const [tab, setTab] = useState<Tab>('book');
  const [step, setStep] = useState<Step>('service');
  const [service, setService] = useState<ServiceView | null>(null);
  const [availability, setAvailability] = useState<AvailabilityView | null>(null);
  const [slot, setSlot] = useState<SlotView | null>(null);
  const [slotDate, setSlotDate] = useState<string | null>(null);
  const [created, setCreated] = useState<AppointmentView | null>(null);
  /** Заполнено — выбор времени переносит существующую запись, а не создаёт новую */
  const [rescheduling, setRescheduling] = useState<AppointmentView | null>(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  /** Запись, по которой сейчас идёт отмена или перенос */
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const showError = useCallback((cause: unknown) => {
    hapticError();
    setError(
      cause instanceof ApiError ? cause.message : 'Что-то пошло не так. Попробуйте ещё раз',
    );
  }, []);

  const loadAppointments = useCallback(async () => {
    if (!botId) {
      return;
    }

    try {
      setAppointments(await api.getAppointments(botId));
    } catch (cause) {
      showError(cause);
    }
  }, [botId, showError]);

  useEffect(() => {
    if (!botId || outsideTelegram) {
      setLoading(false);
      return;
    }

    void (async () => {
      try {
        const [loadedProfile, loadedServices] = await Promise.all([
          api.getProfile(botId),
          api.getServices(botId),
        ]);

        setProfile(loadedProfile);
        setServices(loadedServices);

        // Профиль клиента требует подписи Telegram: в браузере без неё
        // приложение всё равно должно показать услуги и время
        const [loadedMe, loadedAppointments] = await Promise.all([
          api.getMe(botId).catch(() => null),
          api.getAppointments(botId).catch(() => []),
        ]);

        setMe(loadedMe);
        setAppointments(loadedAppointments);
      } catch (cause) {
        showError(cause);
      } finally {
        setLoading(false);
      }
    })();
  }, [botId, outsideTelegram, showError]);

  const resetBooking = useCallback(() => {
    setStep('service');
    setService(null);
    setAvailability(null);
    setSlot(null);
    setSlotDate(null);
    setRescheduling(null);
    setError(null);
  }, []);

  const goBack = useCallback(() => {
    setError(null);

    if (step === 'confirm') {
      setStep('time');
      return;
    }

    if (step === 'time') {
      if (rescheduling) {
        setRescheduling(null);
        setTab('my');
      }
      setStep('service');
      setAvailability(null);
      return;
    }

    resetBooking();
  }, [rescheduling, resetBooking, step]);

  // Кнопка «назад» в шапке Telegram: своя стрелка в интерфейсе выглядела бы
  // чужеродно и дублировала бы системную
  useEffect(() => {
    const backButton = tg?.BackButton;
    if (!backButton) {
      return;
    }

    if (step === 'service') {
      backButton.hide();
      return;
    }

    backButton.onClick(goBack);
    backButton.show();

    return () => {
      backButton.offClick(goBack);
      backButton.hide();
    };
  }, [goBack, step]);

  const openTimes = useCallback(
    async (selected: ServiceView) => {
      if (!botId) {
        return;
      }

      setService(selected);
      setStep('time');
      setBusy(true);
      setError(null);

      try {
        setAvailability(await api.getAvailability(botId, selected.id));
      } catch (cause) {
        showError(cause);
        setStep('service');
      } finally {
        setBusy(false);
      }
    },
    [botId, showError],
  );

  const submitBooking = useCallback(
    async (payload: { comment?: string; phone?: string }) => {
      if (!botId || !service || !slot) {
        return;
      }

      setBusy(true);
      setError(null);

      try {
        const appointment = await api.createAppointment(botId, {
          serviceId: service.id,
          startsAt: slot.startsAt,
          ...payload,
        });

        hapticSuccess();
        setCreated(appointment);
        setStep('success');
        void loadAppointments();
      } catch (cause) {
        showError(cause);

        // Слот заняли, пока клиент заполнял форму: возвращаем к сетке
        // с уже обновлённым временем, иначе он снова выберет занятое
        if (cause instanceof ApiError && cause.code === 'SLOT_ALREADY_TAKEN') {
          setStep('time');
          setAvailability(await api.getAvailability(botId, service.id).catch(() => null));
        }
      } finally {
        setBusy(false);
      }
    },
    [botId, loadAppointments, service, showError, slot],
  );

  const pickSlot = useCallback(
    async (picked: SlotView, date: string) => {
      setSlot(picked);
      setSlotDate(date);

      if (!rescheduling || !botId) {
        setStep('confirm');
        return;
      }

      const confirmed = await confirmAction(
        `Перенести запись «${rescheduling.serviceName}» на ${picked.time}?`,
      );

      if (!confirmed) {
        return;
      }

      setBusy(true);
      setPendingId(rescheduling.id);
      try {
        const moved = await api.rescheduleAppointment(
          botId,
          rescheduling.id,
          picked.startsAt,
        );
        hapticSuccess();
        setCreated(moved);
        setRescheduling(null);
        setStep('success');
        void loadAppointments();
      } catch (cause) {
        showError(cause);
      } finally {
        setBusy(false);
        setPendingId(null);
      }
    },
    [botId, loadAppointments, rescheduling, showError],
  );

  const startReschedule = useCallback(
    async (appointment: AppointmentView) => {
      if (!botId) {
        return;
      }

      setRescheduling(appointment);
      setService(
        services.find((item) => item.id === appointment.serviceId) ?? {
          id: appointment.serviceId,
          name: appointment.serviceName,
          description: null,
          priceKopecks: appointment.priceKopecks,
          priceFormatted: appointment.priceFormatted,
          durationMinutes: appointment.durationMinutes,
          sortOrder: 0,
          isActive: true,
        },
      );
      setTab('book');
      setStep('time');
      setBusy(true);

      try {
        setAvailability(await api.getAvailability(botId, appointment.serviceId));
      } catch (cause) {
        showError(cause);
        resetBooking();
        setTab('my');
      } finally {
        setBusy(false);
      }
    },
    [botId, resetBooking, services, showError],
  );

  const cancelAppointment = useCallback(
    async (appointment: AppointmentView) => {
      if (!botId) {
        return;
      }

      const confirmed = await confirmAction(
        `Отменить запись на ${appointment.date}, ${appointment.time}?`,
      );

      if (!confirmed) {
        return;
      }

      setBusy(true);
      setPendingId(appointment.id);
      try {
        await api.cancelAppointment(botId, appointment.id);
        await loadAppointments();
      } catch (cause) {
        showError(cause);
      } finally {
        setBusy(false);
        setPendingId(null);
      }
    },
    [botId, loadAppointments, showError],
  );

  if (outsideTelegram) {
    return <OutsideTelegram />;
  }

  if (!botId) {
    return (
      <div className="app">
        <div className="empty-state">
          Не понятно, к какому боту относится это приложение. Откройте его кнопкой в чате.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="app">
        <div className="skeleton" />
        <div className="skeleton" />
        <div className="skeleton" />
      </div>
    );
  }

  return (
    <div className="app">
      <div className="header">
        <h1 className="title">{profile?.title ?? 'Запись'}</h1>
      </div>
      {profile?.address && <p className="subtitle">{profile.address}</p>}

      {step === 'service' && (
        <div className="tabs">
          <button
            className={`tab${tab === 'book' ? ' active' : ''}`}
            onClick={() => setTab('book')}
          >
            Записаться
          </button>
          <button
            className={`tab${tab === 'my' ? ' active' : ''}`}
            onClick={() => {
              setTab('my');
              void loadAppointments();
            }}
          >
            Мои записи{appointments.length > 0 ? ` · ${appointments.length}` : ''}
          </button>
        </div>
      )}

      {error && <div className="note error">{error}</div>}

      {tab === 'my' && step === 'service' && (
        <MyAppointments
          appointments={appointments}
          busyId={pendingId}
          onCancel={cancelAppointment}
          onReschedule={startReschedule}
          onBook={() => setTab('book')}
        />
      )}

      {tab === 'book' && step === 'service' && (
        <ServicePicker services={services} onSelect={openTimes} />
      )}

      {step === 'time' &&
        (busy && !availability ? (
          <>
            <div className="skeleton" />
            <div className="skeleton" />
          </>
        ) : (
          availability && <TimePicker availability={availability} onSelect={pickSlot} />
        ))}

      {step === 'confirm' && profile && service && slot && slotDate && (
        <ConfirmBooking
          profile={profile}
          service={service}
          slot={slot}
          date={slotDate}
          knownPhone={me?.phone ?? null}
          busy={busy}
          onSubmit={submitBooking}
        />
      )}

      {step === 'success' && created && (
        <div className="success">
          <div className="success-mark">✓</div>
          <h2 className="title">{created.statusLabel}</h2>
          <p className="subtitle">
            {created.serviceName}
            <br />
            {created.date}, {created.time}
          </p>
          <button
            className="button secondary"
            onClick={() => {
              resetBooking();
              setTab('my');
            }}
          >
            Мои записи
          </button>
        </div>
      )}
    </div>
  );
}
